import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { X, FileText, Lock, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n";
import { getTos, TOS_VERSION, TOS_EFFECTIVE } from "../data/tos";

/**
 * Mandatory Terms-of-Service consent modal that fires before any paid checkout.
 * - User must scroll the ToS body OR click "I have read all terms" to enable the agree checkbox.
 * - User must check the box AND click "Agree & Continue" before checkout starts.
 * - Acceptance is recorded locally (localStorage) AND posted to backend (/api/tos/accept).
 */
export default function TosConsentModal({
  open,
  planLabel = "",
  priceLabel = "",
  trialDays = 30,
  email = "",
  onCancel,
  onAgree,
}) {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const sections = getTos(lang);
  const [scrolled, setScrolled] = useState(false);
  const [checked, setChecked] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      setScrolled(false);
      setChecked(false);
    }
  }, [open]);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) setScrolled(true);
  };

  if (!open) return null;

  const canAgree = scrolled && checked;

  return (
    <div
      className="fixed inset-0 z-[150] bg-black/85 backdrop-blur-md grid place-items-center p-3 sm:p-6 overflow-y-auto"
      data-testid="tos-consent-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-[#0A0A0A] border border-gold-500/40 rounded-xl w-full max-w-[820px] max-h-[92vh] flex flex-col shadow-[0_0_60px_rgba(233,185,52,0.15)]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <FileText size={18} className="text-gold-400" />
            <div>
              <h2 className="font-display text-lg leading-tight">
                {pt ? "Antes de continuar" : "Before you continue"}
              </h2>
              <p className="text-[10px] font-mono text-neutral-500 uppercase tracking-widest">
                {pt ? "Termos de Uso · Versão" : "Terms of Service · Version"} {TOS_VERSION}
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-md text-neutral-500 hover:text-white hover:bg-white/5"
            data-testid="tos-cancel-btn"
            aria-label="close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Plan summary */}
        <div className="px-6 py-3 border-b border-white/5 bg-gold-500/5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Lock size={14} className="text-gold-400" />
              <span className="text-neutral-300">{pt ? "Você está assinando" : "You're subscribing to"}:</span>
              <span className="font-display text-gold-300">{planLabel}</span>
              {priceLabel && <span className="text-neutral-500 font-mono text-xs">· {priceLabel}</span>}
            </div>
            {trialDays > 0 && (
              <div className="text-xs font-mono text-gold-400 uppercase tracking-widest">
                ✦ {trialDays} {pt ? "dias grátis" : "days free"}
              </div>
            )}
          </div>
        </div>

        {/* ToS body — scrollable */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-5 text-sm space-y-5 text-neutral-300 leading-relaxed"
          data-testid="tos-scroll-body"
        >
          <p className="text-xs font-mono text-neutral-500 uppercase tracking-widest mb-3">
            {pt ? "Vigência" : "Effective"}: {TOS_EFFECTIVE}
          </p>
          {sections.map((s, i) => (
            <div key={i} data-testid={`tos-modal-sec-${i}`}>
              <h3 className="font-display text-base text-gold-200 mb-1.5">{s.h}</h3>
              <p className="text-[13px] leading-relaxed text-neutral-400">{s.b}</p>
            </div>
          ))}
          <div className="pt-4 border-t border-white/5 text-xs text-neutral-500">
            {pt ? "Contato: " : "Contact: "}
            <a href="mailto:legal@goldora.global" className="text-gold-400 underline">legal@goldora.global</a>
            {" · "}
            <a href="mailto:support@goldora.global" className="text-gold-400 underline">support@goldora.global</a>
          </div>
        </div>

        {/* Hint when not scrolled */}
        {!scrolled && (
          <div className="px-6 py-2 border-t border-white/5 text-[11px] font-mono text-amber-300/80 bg-amber-500/5 uppercase tracking-widest text-center">
            ↓ {pt ? "Role até o fim para ler todos os termos" : "Scroll to the end to read all terms"}
          </div>
        )}

        {/* Footer — checkbox + actions */}
        <div className="px-6 py-4 border-t border-white/5 bg-black/40">
          <label className={`flex items-start gap-3 cursor-pointer ${!scrolled ? "opacity-50 cursor-not-allowed" : ""}`}>
            <input
              type="checkbox"
              checked={checked}
              disabled={!scrolled}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 w-4 h-4 accent-gold-500 cursor-pointer disabled:cursor-not-allowed"
              data-testid="tos-agree-checkbox"
            />
            <span className="text-sm text-neutral-300 leading-snug">
              {pt
                ? <>Eu li, compreendi e <strong className="text-gold-200">concordo integralmente</strong> com estes Termos de Uso e com a <Link to="/privacidade" className="text-gold-400 underline">Política de Privacidade</Link>. Reconheço que a GOLDORA não fornece aconselhamento financeiro e que sou o único responsável pelas minhas decisões de investimento.</>
                : <>I have read, understood and <strong className="text-gold-200">fully agree</strong> to these Terms of Service and the <Link to="/privacidade" className="text-gold-400 underline">Privacy Policy</Link>. I acknowledge that GOLDORA does not provide financial advice and that I am solely responsible for my investment decisions.</>}
            </span>
          </label>

          <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center sm:justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2.5 text-sm rounded-md border border-white/10 text-neutral-300 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="tos-decline-btn"
            >
              {pt ? "Cancelar" : "Cancel"}
            </button>
            <button
              onClick={() => onAgree?.({ tos_version: TOS_VERSION, accepted_at: new Date().toISOString(), email })}
              disabled={!canAgree}
              data-testid="tos-agree-btn"
              className="px-5 py-2.5 text-sm rounded-md bg-gold-500 hover:bg-gold-400 text-black font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              <CheckCircle2 size={16} />
              {pt ? "Concordar e continuar" : "Agree & Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
